<template>
    <div class="flex flex-col gap-4">
        <div class="flex flex-col">
            <h3 class="text-sm font-medium text-muval-gray-1">Locations</h3>
            <p class="text-xs text-muval-gray-2">
                Where this entry happens - the workshop, the supplier, the site being inspected.
            </p>
        </div>

        <div
            v-if="!disabled && (suggestionsLoading || suggestionsError || suggestions.length)"
            class="flex flex-col gap-3"
        >
            <p
                v-if="suggestionsLoading"
                class="text-sm text-muval-gray-2"
            >
                Loading saved addresses...
            </p>
            <div
                v-else-if="suggestionsError"
                class="flex items-center justify-between gap-3"
            >
                <span class="text-sm text-muval-gray-2">Could not load saved addresses.</span>
                <MuvalButton
                    quaternary
                    type="button"
                    @click="$emit('retry')"
                    >Retry</MuvalButton
                >
            </div>
            <div
                v-else-if="suggestions.length"
                class="flex flex-col gap-2"
            >
                <span class="text-sm font-medium text-muval-gray-1">Saved addresses for this type</span>
                <div
                    v-for="(suggestion, index) in suggestions"
                    :key="index"
                    class="flex items-center justify-between gap-3 rounded-muval-1 border border-muval-gray-4 p-3"
                    data-saved-address
                >
                    <div class="flex min-w-0 flex-col gap-1">
                        <span
                            v-if="suggestion.name"
                            class="text-sm font-medium text-muval-gray-1"
                            >{{ suggestion.name }}</span
                        >
                        <span class="break-words text-sm text-muval-gray-2">{{ suggestion.address }}</span>
                    </div>
                    <span
                        v-if="isAdded(suggestion.address)"
                        class="shrink-0 text-sm text-muval-gray-2"
                        >Added</span
                    >
                    <MuvalButton
                        v-else
                        quaternary
                        type="button"
                        icon="system-plus"
                        @click="addSuggestion(suggestion.address)"
                        >Add</MuvalButton
                    >
                </div>
            </div>
        </div>

        <!--
            Addresses hang off the entry's event, so an entry with no dates has
            nowhere to put them. They are still editable here and are written the
            moment the entry is saved with a window - the alternative, refusing
            input until the dates are set, makes the operator retrace their steps.
        -->
        <p
            v-if="!hasWindow && !disabled"
            class="rounded-muval-2 bg-muval-gray-6 px-3 py-2 text-xs text-muval-gray-2"
        >
            Locations are saved against the entry's dates. Set them on the General tab and these will be kept.
        </p>

        <p
            v-if="rows.length === 0 && disabled"
            class="rounded-muval-2 border border-dashed border-muval-gray-4 px-3 py-6 text-center text-sm text-muval-gray-2"
        >
            No locations on this entry.
        </p>

        <div class="flex flex-col gap-3">
            <div
                v-for="(row, index) in rows"
                :key="row.key"
                class="address-group flex w-full transition-colors hover:border-muval-gray-3"
                :data-address-row="index"
            >
                <template v-if="!disabled">
                    <!-- Reorder buttons -->
                    <div
                        class="arrow-container flex w-8 flex-col border border-r-0 border-muval-gray-4 hover:bg-muval-gray-6"
                    >
                        <button
                            type="button"
                            class="flex flex-1 items-center justify-center hover:bg-muval-gray-5 disabled:cursor-not-allowed disabled:opacity-50"
                            aria-label="Move up"
                            title="Move up"
                            :disabled="index === 0"
                            @click.prevent="move(index, -1)"
                        >
                            <SvgIcon
                                name="system-chevron-up"
                                class="h-3 w-3 text-muval-gray-3"
                            />
                        </button>
                        <button
                            type="button"
                            class="flex flex-1 items-center justify-center hover:bg-muval-gray-5 disabled:cursor-not-allowed disabled:opacity-50"
                            aria-label="Move down"
                            title="Move down"
                            :disabled="index === rows.length - 1"
                            @click.prevent="move(index, 1)"
                        >
                            <SvgIcon
                                name="system-chevron-down"
                                class="h-3 w-3 text-muval-gray-3"
                            />
                        </button>
                    </div>

                    <NewMuvalAddress
                        :vid="`company-diary-address-${row.key}`"
                        class="address flex-1 rounded-none"
                        placeholder="Enter an address"
                        customIcon="system-local"
                        :modelValue="row.value"
                        @update:modelValue="update(index, $event)"
                    />

                    <button
                        type="button"
                        class="remove-btn flex flex-shrink-0 items-center justify-center border border-l-0 border-muval-gray-4 px-3 text-muval-gray-3 hover:bg-muval-gray-6 hover:text-muval-gray-2"
                        aria-label="Remove location"
                        title="Remove location"
                        @click.prevent="remove(index)"
                    >
                        <SvgIcon
                            name="system-delete"
                            class="h-4 w-4"
                        />
                    </button>
                </template>

                <span
                    v-else
                    class="flex h-10 flex-1 items-center gap-2 rounded-muval-1 border border-muval-gray-4 px-3 text-sm text-muval-gray-1"
                >
                    <SvgIcon
                        name="system-local"
                        class="h-4 w-4 shrink-0 text-muval-gray-2"
                    />
                    <span
                        class="truncate"
                        v-text="row.value || '-'"
                    ></span>
                </span>
            </div>
        </div>

        <div
            v-if="!disabled"
            class="flex"
        >
            <MuvalButton
                quaternary
                type="button"
                icon="system-plus"
                @click.prevent="add"
            >
                Add location
            </MuvalButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { CompanyDiarySavedAddress } from '@/api/companyDiary';
import NewMuvalAddress from '@components/inputs/address/NewMuvalAddress.vue';
import SvgIcon from '@components/utilities/SvgIcon.vue';

/**
 * The entry's locations, as an ordered list (spec company-diary-entries, "Entry
 * locations").
 *
 * A flat list rather than the operations diary's start/end/waypoint form: a diary
 * entry has no transit to have ends of, so every address on one is stored as a
 * `LOCATION` and its position in the list is its order. The server stamps both -
 * see `SyncCompanyDiaryEntryAddresses`.
 *
 * Rows carry a stable `key` alongside their text because the text is what the
 * operator is editing: keying the list on the value itself would tear down and
 * rebuild the input on every keystroke, and two blank rows would collide.
 */

const props = withDefaults(
    defineProps<{
        /** The ordered address strings. */
        modelValue: string[];
        /** Whether the entry has dates, and so an event to hang addresses off. */
        hasWindow?: boolean;
        disabled?: boolean;
        suggestions?: CompanyDiarySavedAddress[];
        suggestionsLoading?: boolean;
        suggestionsError?: boolean;
    }>(),
    { hasWindow: false, disabled: false, suggestions: () => [], suggestionsLoading: false, suggestionsError: false },
);

const emit = defineEmits<{
    (e: 'update:modelValue', value: string[]): void;
    (e: 'retry'): void;
}>();

interface Row {
    key: number;
    value: string;
}

let nextKey = 0;
const rows = ref<Row[]>([]);

/** Whether the model has been read once, and so whether an empty list is a fresh one. */
let initialised = false;

/**
 * Rebuilt from the model only when the two actually differ, so a keystroke - which
 * emits and comes back - does not replace the row the operator is typing into and
 * take the caret with it.
 *
 * An editable entry with no addresses is given one blank row to type into, rather
 * than an "Add location" button and nothing else. It is seeded on the first read
 * and on any later rebuild from the model, but never when the model and the rows
 * already agree - which is the state a removal leaves behind, so removing the last
 * row leaves it removed.
 */
watch(
    () => props.modelValue,
    (value) => {
        const incoming = value ?? [];
        const unchanged =
            incoming.length === rows.value.length && incoming.every((item, index) => item === rows.value[index].value);

        if (unchanged && initialised) {
            return;
        }

        initialised = true;

        if (!unchanged) {
            rows.value = incoming.map((item) => ({ key: nextKey++, value: item }));
        }

        if (!props.disabled && rows.value.length === 0) {
            rows.value = [{ key: nextKey++, value: '' }];
        }
    },
    { immediate: true, deep: true },
);

const publish = () =>
    emit(
        'update:modelValue',
        rows.value.map((row) => row.value),
    );

const normalise = (address: string) => address.trim().toLowerCase();
const isAdded = (address: string) => rows.value.some((row) => normalise(row.value) === normalise(address));

const addSuggestion = (address: string) => {
    if (props.disabled || !address.trim() || isAdded(address)) return;
    const blank = rows.value.find((row) => !row.value.trim());
    if (blank) blank.value = address.trim();
    else rows.value.push({ key: nextKey++, value: address.trim() });
    publish();
};

const add = () => {
    rows.value = [...rows.value, { key: nextKey++, value: '' }];
    publish();
};

const update = (index: number, value: string) => {
    rows.value[index].value = value ?? '';
    publish();
};

const remove = (index: number) => {
    rows.value = rows.value.filter((_, position) => position !== index);
    publish();
};

const move = (index: number, offset: number) => {
    const target = index + offset;

    if (target < 0 || target >= rows.value.length) return;

    const next = [...rows.value];
    [next[index], next[target]] = [next[target], next[index]];
    rows.value = next;
    publish();
};
</script>

<style scoped>
/*
 * The row reads as one control: the reorder column, the address input and the
 * remove button share a single border box, so only the outer corners round and
 * the inner edges collapse. Mirrors the operations diary's `AddressEditForm`.
 */
.address {
    :deep(.custom-icon-placeholder) {
        @apply !text-muval-gray-2;
    }

    :deep(input.h-input::placeholder) {
        @apply !text-muval-gray-3;
    }

    :deep(input.h-input) {
        border-radius: 0 !important;
        height: 2.5rem !important;
    }
}

.arrow-container {
    border-top-left-radius: 0.1875rem !important;
    border-bottom-left-radius: 0.1875rem !important;
    height: 2.5rem;
}

.remove-btn {
    border-top-right-radius: 0.1875rem !important;
    border-bottom-right-radius: 0.1875rem !important;
    height: 2.5rem;
}

/* Hovering anywhere on the row lifts the whole border, not just the segment under the cursor. */
.address-group:hover .address :deep(*),
.address-group:hover .remove-btn,
.address-group:hover .arrow-container {
    border-color: #9ca3af !important;
}
</style>
